import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, podatki, type Shema } from '../api/odjemalec';
import { sporocilo } from '../api/napaka';

export type HitroDejanje = NonNullable<Shema['NastavitveZacetne']['hitraDejanja']>[number];
export type KarticaZacetne = NonNullable<Shema['NastavitveZacetne']['skriteKartice']>[number];
export type Nastavitve = Required<Shema['NastavitveZacetne']>;

/** Privzeti vrstni red hitrih dejanj (kot enum HitroDejanje na strežniku), dokler strežnik ne vrne shranjenega. */
export const PRIVZETA_DEJANJA: HitroDejanje[] = [
  'NOV_RACUN',
  'NOV_PREDRACUN',
  'VOZILA',
  'OSEBE',
  'RACUNI',
  'PREDRACUNI',
  'POVPRASEVANJA',
  'ANALIZE',
  'TERMINI',
  'SPREJEM_VOZILA',
  'DELOVNI_NALOGI',
  'VOZILA_STRANK',
  'HOTEL_GUM',
];

/** Kartice s seznami v pregledu poslovanja, ki jih uporabnik lahko skrije, v vrstnem redu na zaslonu. */
export const KARTICE_ZACETNE: { k: KarticaZacetne; naslov: string }[] = [
  { k: 'POVPRASEVANJA', naslov: 'Povpraševanja' },
  { k: 'PREDRACUNI', naslov: 'Predračuni pred iztekom' },
  { k: 'ZANIMANJE', naslov: 'Zanimanje kupcev' },
];

/** Začetni zaslon s strežnika (opozorila, novice, osebne nastavitve); isti ključ uporablja tudi okno »Dolguje« v okvirju. */
export function useZacetna() {
  return useQuery({
    queryKey: ['zacetna'],
    queryFn: ({ signal }) => podatki(api.GET('/api/zacetna', { signal })),
    staleTime: 5 * 60_000,
  });
}

/**
 * Osebne nastavitve začetnega zaslona; {@code undefined}, dokler se nalagajo – takrat številk poslovanja ne kažemo, da
 * se pri uporabniku, ki jih je skril, ne pokažejo za trenutek. Ob napaki nalaganja veljajo privzete.
 */
export function useNastavitveZacetne(): Nastavitve | undefined {
  const zacetna = useZacetna();
  if (!zacetna.data && !zacetna.isError) {
    return undefined;
  }
  const n = zacetna.data?.nastavitve;
  return {
    skrijPregled: n?.skrijPregled ?? false,
    hitraDejanja: n?.hitraDejanja?.length ? n.hitraDejanja : PRIVZETA_DEJANJA,
    skriteKartice: n?.skriteKartice ?? [],
    skritaDejanja: n?.skritaDejanja ?? [],
  };
}

/**
 * Ali je v kartici kaj neogledanega (povpraševanje v stanju NOVO), zaradi česar se pokaže, tudi če jo je uporabnik
 * skril: Povpraševanja ob novih povpraševanjih, Zanimanje kupcev ob vozilu z novim povpraševanjem.
 */
export function neogledano(k: KarticaZacetne, p: Shema['PregledPoslovanja']): boolean {
  if (k === 'POVPRASEVANJA') {
    return (p.povprasevanjaNova ?? 0) > 0;
  }
  if (k === 'ZANIMANJE') {
    return (p.zanimanje ?? []).some((v) => (v.novaPovprasevanja ?? 0) > 0);
  }
  return false;
}

/**
 * Shrani osebne nastavitve začetnega zaslona. Zaslon se spremeni takoj (pred odgovorom strežnika) in ob napaki ne
 * vrne nazaj – skrite številke se ne smejo nenadoma pokazati; uporabnik dobi obvestilo, da nastavitev ni shranjena.
 */
export function useShraniNastavitveZacetne() {
  const odjemalec = useQueryClient();
  const nastavi = (n: Shema['NastavitveZacetne']) =>
    odjemalec.setQueryData<Shema['ZacetniZaslon']>(['zacetna'], (z) => (z ? { ...z, nastavitve: n } : z));
  return useMutation({
    mutationFn: (n: Nastavitve) => podatki(api.PUT('/api/zacetna/nastavitve', { body: n })),
    onMutate: async (n) => {
      await odjemalec.cancelQueries({ queryKey: ['zacetna'] });
      nastavi(n);
    },
    onSuccess: nastavi,
    onError: (e) => notifications.show({ color: 'red', title: 'Nastavitev ni shranjena', message: sporocilo(e) }),
  });
}

/** Seznam z elementom na mestu {@code i} premaknjenim za eno mesto gor (-1) ali dol (1); na robu nespremenjen. */
export function premakni<T>(seznam: readonly T[], i: number, smer: -1 | 1): T[] {
  const j = i + smer;
  const nov = [...seznam];
  if (i < 0 || i >= nov.length || j < 0 || j >= nov.length) {
    return nov;
  }
  [nov[i], nov[j]] = [nov[j]!, nov[i]!];
  return nov;
}

/**
 * Premakne {@code k} za eno mesto med elementi, ki jih {@code vidno} prepušča (skrite preskoči in ostanejo na svojih
 * mestih); na robu ostane, kot je.
 */
export function premakniMed<T>(seznam: readonly T[], k: T, smer: -1 | 1, vidno: (x: T) => boolean): T[] {
  const i = seznam.indexOf(k);
  let j = i + smer;
  while (j >= 0 && j < seznam.length && !vidno(seznam[j]!)) {
    j += smer;
  }
  const nov = [...seznam];
  if (i < 0 || j < 0 || j >= nov.length) {
    return nov;
  }
  [nov[i], nov[j]] = [nov[j]!, nov[i]!];
  return nov;
}

/** Pregled poslovanja na začetnem zaslonu (številke, graf, povpraševanja, predračuni, zanimanje kupcev). */
export function usePregledPoslovanja(omogoceno: boolean) {
  return useQuery({
    queryKey: ['zacetna-pregled'],
    queryFn: ({ signal }) => podatki(api.GET('/api/zacetna/pregled', { signal })),
    enabled: omogoceno,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Rok predračuna glede na današnji dan (lokalni datum brskalnika): »poteče danes«, »poteče jutri«, »poteče čez 2 dni«,
 * »poteklo včeraj«, »poteklo pred 2 dnevoma«; {@code poteklo} za rdečo oznako.
 */
export function rokPredracuna(
  veljavnoDo: string | undefined,
  danes = new Date(),
): { besedilo: string; poteklo: boolean } {
  if (!veljavnoDo) {
    return { besedilo: '', poteklo: false };
  }
  const [l, m, d] = veljavnoDo.split('-').map(Number);
  const rok = Date.UTC(l!, m! - 1, d!);
  const dan = Date.UTC(danes.getFullYear(), danes.getMonth(), danes.getDate());
  const razlika = Math.round((rok - dan) / 86_400_000);
  if (razlika === 0) {
    return { besedilo: 'poteče danes', poteklo: false };
  }
  if (razlika > 0) {
    return { besedilo: razlika === 1 ? 'poteče jutri' : `poteče čez ${razlika} dni`, poteklo: false };
  }
  const n = -razlika;
  return {
    besedilo: n === 1 ? 'poteklo včeraj' : `poteklo pred ${n} ${n === 2 ? 'dnevoma' : 'dnevi'}`,
    poteklo: true,
  };
}
